import { AnyStyledComponent } from 'styled-components'
import React, { FC, memo, useCallback, useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import debounce from 'lodash/debounce'

import { Container, Content, RoomCount, StyledStepper, Text } from './stepper-filter-styles'
import { encodeToBase64 } from '../../../../../utils/encoding-utils'
import { filterGATracking } from '../../utils'
import { FilterItemTransport } from '../../../../../transports/filters-configuration-transport'
import { FilterType } from '../../types'
import { filterClick, getRegionNodeId } from '../../../../../redux/filters'

import { Dispatch } from '../../../../../redux/types'
import { useRequestUserConfigContext } from '../../../../../hooks/request'

import PositiveStepperIcon from '../../../../../assets/icons/plus.svg'
import PositiveStepperIconDisabled from '../../../../../assets/icons/plus-disabled.svg'
import NegativeStepperIcon from '../../../../../assets/icons/minus.svg'
import NegativeStepperIconDisabled from '../../../../../assets/icons/minus-disabled.svg'

type Props = {
    filterItems: FilterItemTransport[]
    label: string
    filterType?: FilterType | string
    shouldCommit?: boolean
}

const StepperFilter: FC<Props> = ({ filterItems, label, filterType = FilterType.BEDROOMS, shouldCommit = false }) => {
    const dispatch = useDispatch<Dispatch>()
    const regionNodeId = useSelector(getRegionNodeId)
    const user = useRequestUserConfigContext()

    // Find the index of the currently checked item. Returns -1 if none checked.
    const checkedItemIndex = useMemo(() => filterItems.findIndex(item => item.checked), [filterItems])

    const [selectedIndex, setSelectedIndex] = useState(checkedItemIndex)

    // Sync local state when external selection changes
    useEffect(() => {
        setSelectedIndex(checkedItemIndex)
    }, [checkedItemIndex])

    const currentValue = selectedIndex >= 0 && selectedIndex < filterItems.length ? filterItems[selectedIndex].value : 0
    const hasPrevious = selectedIndex > 0
    // If nothing selected (-1), we can go to 0, so hasNext is true if there are items.
    const hasNext = selectedIndex < filterItems.length - 1

    const submitFilterSelection = useCallback(
        (index: number) => {
            if (index >= 0 && index < filterItems.length) {
                const item = filterItems[index]
                filterGATracking({
                    type: filterType as FilterType,
                    name: item.title,
                    value: item.value,
                    isChecked: true,
                    regionNodeId,
                })
                const filterItemUrl = new URL(item.absoluteLink)
                dispatch(filterClick(encodeToBase64(filterItemUrl.pathname + filterItemUrl.search), shouldCommit, user))
            }
        },
        [filterItems, filterType, regionNodeId, dispatch, user, shouldCommit],
    )

    const debouncedSubmitFilterSelection = useMemo(() => debounce(submitFilterSelection, 700), [submitFilterSelection])

    useEffect(() => {
        return () => {
            debouncedSubmitFilterSelection.cancel()
        }
    }, [debouncedSubmitFilterSelection])

    const handleNegativeStepperClick = useCallback(() => {
        if (hasPrevious) {
            const newIndex = selectedIndex - 1
            setSelectedIndex(newIndex)
            debouncedSubmitFilterSelection(newIndex)
        }
    }, [hasPrevious, selectedIndex, debouncedSubmitFilterSelection])

    const handlePositiveStepperClick = useCallback(() => {
        if (hasNext) {
            const newIndex = selectedIndex + 1
            setSelectedIndex(newIndex)
            debouncedSubmitFilterSelection(newIndex)
        }
    }, [hasNext, selectedIndex, debouncedSubmitFilterSelection])

    return (
        <Container>
            <Text>{label} </Text>
            <Content>
                {hasPrevious ? (
                    <StyledStepper
                        onClick={handleNegativeStepperClick}
                        as={NegativeStepperIcon as AnyStyledComponent}
                        alt="negative stepper icon"
                    />
                ) : (
                    <StyledStepper as={NegativeStepperIconDisabled as AnyStyledComponent} />
                )}

                <RoomCount> {currentValue}</RoomCount>
                {hasNext ? (
                    <StyledStepper
                        onClick={handlePositiveStepperClick}
                        as={PositiveStepperIcon as AnyStyledComponent}
                        alt="positive stepper icon"
                    />
                ) : (
                    <StyledStepper as={PositiveStepperIconDisabled as AnyStyledComponent} alt="disabled stepper icon" />
                )}
            </Content>
        </Container>
    )
}

export default memo(StepperFilter)
